import smoothscroll from 'smoothscroll-polyfill'
import React, { Component, Fragment } from 'react'
import { bool, object, arrayOf, string } from 'prop-types'
import { delve } from '@mylo/dlv-and-dset'
import { loadState, saveState, LOAD } from './storage'
import { createStore, combineReducers, applyMiddleware } from 'redux'
import {
  ConnectedRouter,
  routerReducer,
  routerMiddleware
} from 'react-router-redux'
import { Provider } from 'react-redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import { ThemeProvider as MuiThemeProvider } from '@mui/material'
import { reducer as formReducer, actionTypes } from 'redux-form'
import thunk from 'redux-thunk'
import createHistory from 'history/createHashHistory'
import reduxIdleDispatcher from 'redux-idle-dispatcher'
import debounce from '../common/utility/debounce'
import { ThemeProvider } from 'styled-components'

import { scTheme as theme, myloTheme } from './theme'
import Gate from './Gate'
import AgentBanner from './components/AgentBanner'
import AuditWrapper from './components/AuditWrapper'

import './styles/buyflow.css'
import { StylesProvider, createGenerateClassName } from '@mui/styles'

const generateClassName = createGenerateClassName({
  seed: 'b',
  productionPrefix: 'b'
})

smoothscroll.polyfill()

const DEFAULT_STORAGE_KEY = 'mylo-buyflow-storage'

export default class App extends Component {
  static propTypes = {
    enableStorage: bool,
    enableErrorMonitor: bool,
    reducers: object,
    plugins: object,
    storageKey: string,
    storageBlacklist: arrayOf(arrayOf(string)),
    storageWhitelist: arrayOf(arrayOf(string))
  }

  static defaultProps = {
    enableStorage: true,
    enableErrorMonitor: false,
    reducers: {},
    plugins: {},
    storageKey: DEFAULT_STORAGE_KEY,
    storageBlacklist: [],
    storageWhitelist: []
  }

  state = {
    isAdminModule: false,
    ready: true,
    store: null,
    history: createHistory(),
    idleUnsubscriber: null
  }

  setup = ({
    enableStorage,
    enableErrorMonitor,
    reducers,
    plugins,
    storageKey,
    ...props
  }) => {
    const { history } = this.state
    const reducer = combineReducers({
      ...reducers,
      form: formReducer.plugin(plugins),
      router: routerReducer
    })
    const middlewaresToUse = [thunk, routerMiddleware(history)]
    if (enableErrorMonitor) {
      middlewaresToUse.push(errorMonitor)
    }
    const middleware = process.env.NODE_ENV !== 'production' ? composeWithDevTools(
      applyMiddleware(...middlewaresToUse)
    ) : applyMiddleware(...middlewaresToUse)

    const loadedState = enableStorage
      ? loadState({
        storageKey,
        ...props
      })
      : {}
    const store = createStore(reducer, loadedState, middleware)

    if (enableStorage) {
      store.dispatch({
        type: LOAD,
        payload: loadedState
      })
    }

    const idleUnsubscriber = reduxIdleDispatcher(store, 15000)

    if (enableStorage) {
      store.subscribe(
        debounce(() => {
          saveState({
            storageKey,
            ...props,
            state: store.getState()
          })
        }, 1000)
      )
    }

    this.setState(prevState => ({
      ...prevState,
      isAdminModule: !!delve(window, 'clientOptions.user'),
      store,
      idleUnsubscriber,
      ready: true
    }))
  }

  UNSAFE_componentWillMount () {
    this.setup(this.props)
  }

  UNSAFE_componentWillReceiveProps (nextProps) {
    this.setup(nextProps)
  }

  componentWillUnmount () {
    if (this.state.idleUnsubscriber) {
      this.state.idleUnsubscriber()
    }
  }

  render () {
    const { store, history, ready, isAdminModule } = this.state
    if (!ready) {
      return null
    }
    const {
      children,
      AgentBannerTag = isAdminModule ? Fragment : AgentBanner,
      ...props
    } = this.props
    return (
      <StylesProvider generateClassName={generateClassName}>
        <MuiThemeProvider theme={myloTheme}>
          <ThemeProvider theme={theme}>
            <Provider store={store}>
              <ConnectedRouter history={history}>
                <AgentBannerTag>
                  <AuditWrapper>
                    {!isAdminModule && <Gate {...props} {...this.state} />}
                    {React.Children.map(children, child =>
                      React.cloneElement(child, { ...props, ...child.props, store })
                    )}
                  </AuditWrapper>
                </AgentBannerTag>
              </ConnectedRouter>
            </Provider>
          </ThemeProvider>
        </MuiThemeProvider>
      </StylesProvider>
    )
  }
}

function errorMonitor (_store) {
  return next => action => {
    if (action.type === actionTypes.SET_SUBMIT_FAILED && action.error) {
      try {
        window.requestAnimationFrame(() => {
          // see if we can find an error
          const errorClasses =
            '.flow__field--invalid,.flow__field--error,.form__hint--error'
          const error = document.querySelector(errorClasses)

          // try to find the parent field of the error
          const fieldClasses = '.flow__field,.form__field'
          const field = error ? error.closest(fieldClasses) : null

          if (field) {
            window.requestAnimationFrame(() => {
              // scroll to the window to the top of the field
              window.scrollTo(0, field.offsetTop)
              window.requestAnimationFrame(() => {
                // focus the field's input, if available
                const input = field.querySelector('input,select,textarea')
                if (input) {
                  input.focus()
                }
              })
            })
          }
        })
      } catch (e) {} // swallow any errors here
    }

    return next(action)
  }
}
